import { openDB } from "idb"

const versions = {
    resourceChartDB: 100,
    myExpensesDb: 101,
    ttSummaryDb: 100,
    ttLeavesDb: 100,
    myOrgPeople: 103, // 102: people now carry contractHistory (leave balances per contract)
    orgAccessDetails: 100,
    timesheetsOrgUltraLite: 100,
    orgLeavesDbOrgTime: 101,
    orgLeavesDbOrgTimePerson: 101,
    orgMissionDb: 100,
    orgPlanItemDb: 100,
    orgActionDb: 100,
    orgActionItemDb: 100,
    orgExchangeRates: 100,
    orgHolidays: 101,
    orgLeavesDb: 101,
    orgPeoplesLeavesDb: 101,
    orgResourceChart: 100,
    pmLeaves: 101,
    pmOrgLeaves: 101,
    pmTimesheets: 100,
    pmTimesheetMissions: 100,
    pmTimesheetPlanItems: 100,
    timesheetMissionDb: 100,
    timesheetOrgDb: 100,
    timesheetOrgDbFull: 100,
    timesheetMissionDbFull: 100,
    orgMissionsFinancials: 103, // 103: keyed per person (was org only)
    orgMissionFinancialsMissions: 103, // 102: missions now carry isProcess/notBillable/plan dates; 103: costCenter
    orgMissionFinancialsPlanItems: 102,
    orgMissionFinancialsActions: 102,
    orgMissionFinancialsActionItems: 102, // 102: action items now carry startDate (budget-lite)
    orgMissionFinancialsTimesheets: 102, // 102: window now covers weeks straddling the range edges
    orgFinancialsChart: 101,
    orgRoadMapMissions: 101,
    orgRoadMapPlanItems: 100,
    orgRoadMapActions: 100,
    orgRoadMapActionItems: 101, // 101: action items now carry startDate (budget-lite)
    orgClientsDb: 100,
    orgClientsLiteDb: 101, // 101: clear lists cached empty after a failed fetch
    orgFinancialsDb: 101,
    orgClientsFullDb: 100,
    approvedTimesheetsDB: 100,
    approvedTimesheetsPmDB: 100,
    secureKeys: 100,
    pmTaskAssignments: 100,
    pmAllocations: 100,
    myTimesheets: 100,
    myRoles: 100,
    myTimesheetUpdates: 100,
    projectPlannerMissions: 100,
    projectPlannerPlanItems: 100,
    projectPlannerActions: 101, // 101: actions carry isSection/source/dependencies (board sections are actions)
    projectPlannerActionItems: 100,
    costTrackerMissions: 100,
    costTrackerPlanItems: 100,
    costTrackerActions: 100,
    costTrackerActionItems: 100,
    costTrackerOrgMissions: 100,
    costTrackerOrgPlanItems: 100,
    costTrackerOrgActions: 100,
    costTrackerOrgActionItems: 100,
    pmmMissions: 100,
    pmmPlanItems: 100,
    pmmActions: 100,
    pmmActionItems: 100,
    pmmFinancials: 100,
    missionTimesheets: 100,
    myLeaves: 101,
    schedulingMissionsPm: 102, // 102: missions carry org (replaces the "-v3" key suffix)
    schedulingPlanItemsPm: 103, // 102: plan items now carry createdFromGtCrm (CRM roles are left out); 103: "-v3" key suffix dropped
    orgBPM: 100,
    orgMissionCountsDb: 100,
    orgResourceMissionsDb: 100,
    orgResourceFinancialsDb: 100,
    orgClientFinancialsDb: 100,
    orgClientInvoicesDb: 100,
    timesheetsOrgTimeGroups: 100,
    timesheetsOrgCompanyUltraLite: 100,
    timesheetsOrgProjectUltraLite: 100,
    missionBasicDetails: 101,
    orgFinancialsMissions: 100,
    orgFinancialsPlanItems: 100,
    orgFinancialsActions: 100,
    orgFinancialsActionItems: 100,
    orgFinancialsTimesheets: 100,
    clientBasicDetails: 101,
    timesheetAnalyticsTotalsDb: 100,
    timesheetsByMissionApprovedHours: 100,
    orgApprovedBugetTimesheets: 101, // 101: months now load whole weeks (weeks crossing a month edge were dropped)
    orgUtilizationTimesheets: 100,
    orgUtilizationMonths: 100,
    orgWfhDb: 100,
    orgLeaveDetailsDb: 101,
    orgPartnersMissionsDb: 100,
    orgPartnersPlanItemsDb: 100,
    timeTrackingProjectTasks: 100,
    orgIndividualsLeaves: 101,
    ttMyMissions: 100,
    ttMyClients: 100,
    ttMyWeekHours: 100,
    orgCacheScope: 100,
}

/**
 * Caches that hold nothing an org permission, accessible office/department or can* flag
 * decides. Every other cache above is emptied by clearScopedCaches when a person's access
 * changes, so a new cache is purged unless it is listed here.
 */
export const SCOPE_FREE_DBS = new Set([
    "secureKeys", // the encryption key every other cache is read with
    "orgCacheScope", // the access each org's caches were filled under (utils/cacheScope.js)
    "orgExchangeRates",
    "orgHolidays",
    "ttMyMissions",
    "ttMyClients",
    "ttMyWeekHours",
])

/**
 * Every connection we open must let go the moment anything - this tab or another one -
 * wants to delete or upgrade its database. An open connection that ignores that request
 * leaves the delete/upgrade pending, and every later open of that database (a refreshed
 * tab, a version bump after a deploy, the re-open right after deleteEntireDb) queues
 * behind it forever. That was the endless "Preparing workspace" with several tabs open.
 */
const yieldToOtherTabs = () => ({
    blocking(currentVersion, blockedVersion, event) {
        try {
            event.target.close()
        } catch (e) {}
    },
})

/**
 * The cache is never worth waiting on: an open that is still queued after this long is
 * stuck behind a connection somebody else will not let go of (typically a tab still on an
 * older build). Reads then miss and writes are skipped, so the screen loads from the API.
 */
export const OPEN_TIMEOUT_MS = 5000

const CACHE_UNAVAILABLE = "CacheUnavailable"

/**
 * One open connection per database for the whole tab, shared by every call.
 *
 * A connection that listens for versionchange (yieldToOtherTabs) is never garbage
 * collected while it is open, so opening one per read/write - as initDB used to - piles
 * up hundreds of live connections over a session. Sharing one keeps it at one per
 * database. It is dropped here the moment it closes (another tab's delete/upgrade, the
 * browser closing it, deleteEntireDb in this tab) and the next call opens a fresh one.
 *
 * dbName -> { db, ready: Promise<IDBPDatabase> }
 */
const connections = new Map()

// Only ever drops the entry it is told about, never a newer one that has replaced it
const forgetConnection = (dbName, held) => {
    if (held && connections.get(dbName) === held) {
        connections.delete(dbName)
    }
}

/**
 * Closes this tab's connection to a database (if any) and forgets it, so a delete of
 * that database is never held up by this tab.
 */
export const closeConnection = (dbName) => {
    const held = connections.get(dbName)

    if (!held) return

    connections.delete(dbName)
    held.ready.then((db) => db.close()).catch(() => {})
}

const openConnection = (dbName, storeName, keyPath) => {
    const held = { db: null, ready: null }

    const opening = (async () => {
        let ver = versions[dbName]

        // Ensure we never open with a lower version than existing
        const dbList = (await indexedDB.databases?.()) || []
        const existing = dbList.find((db) => db.name === dbName)
        if (existing && existing.version && ver < existing.version) {
            ver = existing.version
        }

        return openDB(dbName, ver, {
            upgrade(upgradeDB, oldVersion, newVersion, transaction) {
                if (oldVersion < newVersion) {
                    for (const name of upgradeDB.objectStoreNames) {
                        upgradeDB.deleteObjectStore(name)
                    }
                }

                if (!upgradeDB.objectStoreNames.contains(storeName)) {
                    upgradeDB.createObjectStore(storeName, { keyPath })
                }
            },
            blocking(currentVersion, blockedVersion, event) {
                forgetConnection(dbName, held)
                yieldToOtherTabs().blocking(currentVersion, blockedVersion, event)
            },
            // The browser closed it (storage cleared, disk error)
            terminated() {
                forgetConnection(dbName, held)
            },
        })
    })()

    let timer
    const timeout = new Promise((resolve, reject) => {
        timer = setTimeout(() => {
            const err = new Error(`Cache "${dbName}" did not open within ${OPEN_TIMEOUT_MS}ms`)
            err.name = CACHE_UNAVAILABLE
            reject(err)
        }, OPEN_TIMEOUT_MS)
    })

    held.ready = Promise.race([opening, timeout]).then(
        (db) => {
            clearTimeout(timer)
            held.db = db
            return db
        },
        (err) => {
            clearTimeout(timer)
            forgetConnection(dbName, held)
            // An open that gets through after we gave up on it is nobody's: close it
            // rather than leave it pinned open.
            opening.then((db) => db.close()).catch(() => {})
            throw err
        },
    )

    return held
}

export async function initDB(dbName, storeName, keyPath = "id") {
    if (!versions[dbName]) throw new Error(`Version DB missing for ${dbName}`)

    let held = connections.get(dbName)

    if (!held) {
        held = openConnection(dbName, storeName, keyPath)
        connections.set(dbName, held)
    }

    const db = await held.ready

    if (!db.objectStoreNames.contains(storeName)) {
        throw new Error(`Store "${storeName}" was not created`)
    }

    return db
}

/**
 * Runs a piece of work on the shared connection. Should the connection have closed under
 * it (InvalidStateError: closed by another tab's delete/upgrade, or by a caller's close())
 * it is forgotten and the work runs once more on a fresh one.
 */
const withConnection = async (dbName, storeName, work) => {
    const db = await initDB(dbName, storeName)

    try {
        return await work(db)
    } catch (err) {
        if (err?.name !== "InvalidStateError") throw err

        const held = connections.get(dbName)
        if (held?.db === db) forgetConnection(dbName, held)

        return work(await initDB(dbName, storeName))
    }
}

const isCacheUnavailable = (err) => err?.name === CACHE_UNAVAILABLE

export const addDbItem = async (dbName, storeName, item) => {
    // Mirrors the null-key guard in getDbItem: a cache write with no key can never be
    // read back, so skip it rather than throwing. Throwing here surfaced as an unhandled
    // rejection from the (un-awaited) callers. Warns so the offending store stays visible.
    if (!item?.id) {
        console.warn(`addDbItem: skipped write with no "id"`, dbName, storeName)
        return undefined
    }

    try {
        await withConnection(dbName, storeName, (db) => db.put(storeName, item))

        return item
    } catch (error) {
        if (isCacheUnavailable(error)) {
            console.warn(error.message, "- write skipped")
            return undefined
        }

        throw error
    }
}

// Get an item by ID from the database with a dynamic store name
export const getDbItem = async (dbName, storeName, id, withDb) => {
    try {
        // A lookup with no key is a cache miss, not an error. Without this, db.get throws
        // DataError and the rethrow below surfaces as an unhandled rejection that kills the
        // calling loader (e.g. a loadData that reads person._id before person has hydrated).
        if (id === undefined || id === null) {
            return withDb ? { db: await initDB(dbName, storeName), item: undefined } : undefined
        }

        if (withDb) {
            let db
            const item = await withConnection(dbName, storeName, (conn) => {
                db = conn
                return conn.get(storeName, id)
            })

            // db is this tab's shared connection: callers must not close it
            return {
                db,
                item,
            }
        }

        return await withConnection(dbName, storeName, (db) => db.get(storeName, id)) // Fetch item by ID
    } catch (error) {
        if (isCacheUnavailable(error)) {
            console.warn(error.message, "- read as a miss")
            return withDb ? { db: null, item: undefined } : undefined
        }

        console.log(error)

        throw error
    }
}

// Get all items from the database with a dynamic store name
export const getAllItems = async (dbName, storeName) => {
    try {
        return await withConnection(dbName, storeName, (db) => db.getAll(storeName)) // Fetch all items
    } catch (error) {
        if (isCacheUnavailable(error)) {
            console.warn(error.message, "- read as a miss")
            return []
        }

        throw error
    }
}

// Delete an item by ID from the database with a dynamic store name
export const deleteDbItem = async (dbName, storeName, id) => {
    try {
        return await withConnection(dbName, storeName, (db) => db.delete(storeName, id)) // Delete item by ID
    } catch (error) {
        if (isCacheUnavailable(error)) {
            console.warn(error.message, "- delete skipped")
            return undefined
        }

        console.error("Failed to delete item:", error)
        throw error
    }
}

export const deleteEntireDb = async (dbName) => {
    // This tab's own connection must not be what holds the delete up
    closeConnection(dbName)

    return new Promise((resolve, reject) => {
        const request = indexedDB.deleteDatabase(dbName)

        request.onerror = (event) => {
            console.error("Error deleting database:", event.target.error)
            reject(event.target.error)
        }

        request.onsuccess = (event) => {
            resolve()
        }

        request.onblocked = (c) => {
            resolve()
        }
    })
}

/**
 * Empties every object store of a database without deleting the database itself.
 * deleteDatabase is what used to hang other tabs: it waits for every open connection
 * in every tab to close, Firebase's and idb-keyval's never do, and while it waits any
 * new open of that database - e.g. Firebase auth in a refreshed tab - queues behind it
 * forever. Clearing works alongside open connections. Gives up after a few seconds so
 * sign-out can never hang on storage either. Resolves true once everything is cleared.
 */
export function clearDbContents(name) {
    const clearing = new Promise((resolve) => {
        const request = indexedDB.open(name)
        request.onerror = () => resolve(false)
        request.onblocked = () => resolve(false)
        request.onsuccess = () => {
            const db = request.result
            db.onversionchange = () => db.close()
            const stores = Array.from(db.objectStoreNames)
            if (!stores.length) {
                db.close()
                return resolve(true)
            }
            try {
                const tx = db.transaction(stores, "readwrite")
                stores.forEach((store) => tx.objectStore(store).clear())
                tx.oncomplete = () => {
                    db.close()
                    resolve(true)
                }
                tx.onerror = tx.onabort = () => {
                    db.close()
                    resolve(false)
                }
            } catch (err) {
                db.close()
                resolve(false)
            }
        }
    })

    return Promise.race([clearing, new Promise((resolve) => setTimeout(() => resolve(false), 3000))])
}

/**
 * Empties every cache not in SCOPE_FREE_DBS, for when what the person may see has
 * changed. Only databases that exist are opened, so none is created just to be emptied.
 * Resolves true once all of them are cleared.
 */
export const clearScopedCaches = async () => {
    const existing = await indexedDB.databases?.()
    const existingNames = existing ? new Set(existing.map((db) => db.name)) : null

    const names = Object.keys(versions).filter(
        (name) => !SCOPE_FREE_DBS.has(name) && (!existingNames || existingNames.has(name)),
    )

    const results = await Promise.all(names.map(clearDbContents))

    return results.every(Boolean)
}

// Not shared like initDB's: the caller owns this connection and must close() it
export const initDBWithIndexes = async (dbName, storeName, keyPath = "id", indexes = []) => {
    const ver = versions[dbName]

    return openDB(dbName, ver, {
        upgrade(db, oldVersion, newVersion, transaction) {
            let store

            if (!db.objectStoreNames.contains(storeName)) {
                // Create the store if it doesn't exist
                store = db.createObjectStore(storeName, { keyPath })
            } else if (oldVersion < newVersion) {
                // Clear the store's data when the version changes
                store = transaction.objectStore(storeName)
                store.clear()
            } else {
                // Get the store for index creation
                store = transaction.objectStore(storeName)
            }

            // Create indexes if provided
            indexes.forEach(({ name, keyPath, options }) => {
                if (!store.indexNames.contains(name)) {
                    store.createIndex(name, keyPath, options)
                }
            })
        },
        ...yieldToOtherTabs(),
    })
}

export const deleteIndexedDB = (dbName) => {
    return new Promise((resolve, reject) => {
        if (!dbName || typeof dbName !== "string") {
            reject(new Error("Invalid database name"))
            return
        }

        // This tab's own connection must not be what blocks the delete
        closeConnection(dbName)

        const request = indexedDB.deleteDatabase(dbName)

        request.onsuccess = () => {
            // ⚡ Some browsers resolve success even if DB never existed
            if (request.result === undefined) {
                resolve(`Database "${dbName}" did not exist, but operation is complete.`)
            } else {
                resolve(`Database "${dbName}" deleted successfully.`)
            }
        }

        request.onerror = (event) => {
            const message = event.target.error?.message || event.target.error || "Unknown error"
            reject(new Error(`Error deleting database "${dbName}": ${message}`))
        }

        request.onblocked = () => {
            reject(new Error(`Cannot delete "${dbName}": database is still open in another tab or connection.`))
        }
    })
}

// indexedDBUtils.js
export const deleteRecordsByUserId = (dbName, storeName, userId) => {
    return new Promise((resolve, reject) => {
        if (!dbName || !storeName || !userId) {
            reject(new Error("Missing required parameters: dbName, storeName, or userId"))
            return
        }

        const request = indexedDB.open(dbName)

        request.onerror = (event) => {
            reject(new Error(`Failed to open database: ${event.target.error?.message || event}`))
        }

        request.onsuccess = (event) => {
            const db = event.target.result
            db.onversionchange = () => db.close()

            // ✅ Guard: check if store exists
            if (!db.objectStoreNames.contains(storeName)) {
                db.close()
                reject(new Error(`Object store "${storeName}" not found in database "${dbName}"`))
                return
            }

            let deletedCount = 0
            const transaction = db.transaction([storeName], "readwrite")
            const store = transaction.objectStore(storeName)
            const cursorRequest = store.openCursor()

            cursorRequest.onsuccess = (event) => {
                const cursor = event.target.result
                if (cursor) {
                    if (String(cursor.key).includes(userId)) {
                        store.delete(cursor.key)
                        deletedCount++
                    }
                    cursor.continue()
                }
            }

            cursorRequest.onerror = (event) => {
                db.close()
                reject(new Error(`Error iterating store: ${event.target.error?.message || event}`))
            }

            transaction.oncomplete = () => {
                db.close()
                resolve(`Deleted ${deletedCount} record(s) for userId "${userId}" in store "${storeName}"`)
            }

            transaction.onerror = (event) => {
                db.close()
                reject(new Error(`Transaction failed: ${event.target.error?.message || event}`))
            }
        }

        request.onblocked = () => {
            reject(new Error(`Database "${dbName}" is blocked. Close all connections.`))
        }
    })
}
